import { useState, useEffect } from "react";
import { Lock, Plus, Trash2, Edit2, MapPin, Clock, Users, Calendar, Baby, LogOut, Settings, X, Upload, ChevronDown, ChevronUp, Type, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import EventsAdminTab from "../components/EventsAdminTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLASS_TYPES = {
  under_2: { name: "Under 2 Years", price: 8.50 },
  "2_years": { name: "2 Years", price: 9.50 },
  "3_4_years": { name: "3-4 Years", price: 9.50 },
  under_4: { name: "Under 4 Years", price: 9.00 },
  gyminis: { name: "Gyminis", price: 9.50 }
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const DEFAULT_HOURS = DAYS.map(day => ({
  day,
  open_time: day === "Sunday" ? "" : "09:00",
  close_time: day === "Sunday" ? "" : (day === "Saturday" ? "12:00" : "16:00"),
  is_open: day !== "Sunday"
}));

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pin, setPin] = useState("");
  const [classes, setClasses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [activeTab, setActiveTab] = useState("classes");
  
  // Opening hours state
  const [openingHours, setOpeningHours] = useState({});
  const [editingLocation, setEditingLocation] = useState(null);
  const [hoursForm, setHoursForm] = useState([]);
  
  // Closed dates state
  const [closedDates, setClosedDates] = useState([]);
  const [showAddClosed, setShowAddClosed] = useState(false);
  const [closedForm, setClosedForm] = useState({ date: "", reason: "Bank Holiday", applies_to: ["all"] });
  
  // Term dates state
  const [termDates, setTermDates] = useState([]);
  const [showAddTerm, setShowAddTerm] = useState(false);
  const [termForm, setTermForm] = useState({ name: "", start_date: "", end_date: "" });
  
  // Locations state
  const [locations, setLocations] = useState([]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  const [locForm, setLocForm] = useState({ name: "", display_name: "", address: "", postcode: "", lat: null, lng: null });
  
  // Coaches state
  const [coaches, setCoaches] = useState([]);
  const [showAddCoach, setShowAddCoach] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [coachForm, setCoachForm] = useState({ full_name: "", position: "", bio: "", qualifications: "", order: 0 });
  const [coachImage, setCoachImage] = useState(null);

  // FAQs state
  const [faqs, setFaqs] = useState([]);
  const [showAddFAQ, setShowAddFAQ] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: "", answer: "", order: 0 });

  // Footer text state
  const [footerText, setFooterText] = useState({ tagline: "", copyright_text: "" });

  // Social links state
  const [socialLinks, setSocialLinks] = useState({ facebook_url: "", instagram_url: "" });

  // What to Expect state
  const [whatToExpect, setWhatToExpect] = useState([]);
  const [editingWTE, setEditingWTE] = useState(null);
  const [wteForm, setWteForm] = useState({ icon: "Shirt", title: "", content: "", accent: "red" });

  // Birthday Parties state
  const [birthdayParties, setBirthdayParties] = useState([]);
  const [partyForm, setPartyForm] = useState({ id: "", name: "", member_price: "", non_member_price: "" });
  const [editingParty, setEditingParty] = useState(null);

  // Terms state
  const [termsSections, setTermsSections] = useState([]);
  const [editingTermIdx, setEditingTermIdx] = useState(null);
  const [tcForm, setTcForm] = useState({ title: "", items: "" });

  // Page content state
  const [aboutContent, setAboutContent] = useState({});
  const [contactContent, setContactContent] = useState({});
  const [heroContent, setHeroContent] = useState({});

  // Booking links state
  const [bookingLinks, setBookingLinks] = useState({});

  // Class form state
  const [formData, setFormData] = useState({
    class_type: "under_2",
    location_id: "",
    location_address: "",
    location_display: "",
    location_lat: null,
    location_lng: null,
    day_of_week: "Monday",
    time: "09:30",
    duration_minutes: 45,
    min_age_months: 0,
    max_age_months: 23,
    with_parent: true
  });
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);

  const handleLogin = async () => {
    try {
      const response = await axios.post(`${API}/admin/login`, { pin });
      if (response.data.success) {
        setIsLoggedIn(true);
        localStorage.setItem("adminPin", pin);
        toast.success("Welcome to Admin!");
        fetchAll();
      } else {
        toast.error("Invalid PIN");
      }
    } catch (error) {
      toast.error("Login failed");
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setPin("");
    localStorage.removeItem("adminPin");
  };

  const fetchAll = () => {
    fetchClasses();
    fetchOpeningHours();
    fetchClosedDates();
    fetchTermDates();
    fetchLocations();
    fetchCoaches();
    fetchFAQs();
    fetchFooterText();
    fetchSocialLinks();
    fetchWhatToExpect();
    fetchBirthdayParties();
    fetchTerms();
    fetchPageContent();
    fetchBookingLinks();
  };

  const fetchClasses = async () => {
    try {
      const storedPin = localStorage.getItem("adminPin");
      const response = await axios.get(`${API}/admin/classes?pin=${storedPin}`);
      setClasses(response.data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchOpeningHours = async () => {
    try {
      const response = await axios.get(`${API}/opening-hours`);
      const hoursMap = {};
      response.data.forEach(loc => {
        hoursMap[loc.location_id] = loc.hours;
      });
      setOpeningHours(hoursMap);
    } catch (error) {
      console.error("Error fetching hours:", error);
    }
  };

  const fetchClosedDates = async () => {
    try {
      const storedPin = localStorage.getItem("adminPin");
      const response = await axios.get(`${API}/admin/closed-dates?pin=${storedPin}`);
      setClosedDates(response.data);
    } catch (error) {
      console.error("Error fetching closed dates:", error);
    }
  };

  const fetchTermDates = async () => {
    try {
      const storedPin = localStorage.getItem("adminPin");
      const response = await axios.get(`${API}/admin/term-dates?pin=${storedPin}`);
      setTermDates(response.data);
    } catch (error) {
      console.error("Error fetching term dates:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const storedPin = localStorage.getItem("adminPin");
      const response = await axios.get(`${API}/admin/locations?pin=${storedPin}`);
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  useEffect(() => {
    const storedPin = localStorage.getItem("adminPin");
    if (storedPin) {
      setPin(storedPin);
      setIsLoggedIn(true);
      fetchAll();
    }
  }, []);

  // Class handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    const storedPin = localStorage.getItem("adminPin");
    
    try {
      if (editingClass) {
        await axios.put(`${API}/admin/classes/${editingClass.id}?pin=${storedPin}`, formData);
        toast.success("Class updated!");
      } else if (bulkMode && selectedDays.length > 0) {
        // Bulk add: create one class per selected day
        let created = 0;
        for (const day of selectedDays) {
          await axios.post(`${API}/admin/classes?pin=${storedPin}`, { ...formData, day_of_week: day });
          created++;
        }
        toast.success(`${created} class${created > 1 ? "es" : ""} added!`);
      } else {
        await axios.post(`${API}/admin/classes?pin=${storedPin}`, formData);
        toast.success("Class added!");
      }
      fetchClasses();
      setShowAddForm(false);
      setEditingClass(null);
      resetForm();
    } catch (error) {
      toast.error("Failed to save class");
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm("Delete this class?")) return;
    
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.delete(`${API}/admin/classes/${classId}?pin=${storedPin}`);
      toast.success("Class deleted!");
      fetchClasses();
    } catch (error) {
      toast.error("Failed to delete class");
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    // Try to match the class's location to a saved location
    const matchedLoc = locations.find(l =>
      l.address === cls.location_address || l.display_name === cls.location_display
    );
    setFormData({
      class_type: cls.class_type,
      location_id: matchedLoc ? matchedLoc.id : "",
      location_address: cls.location_address,
      location_display: cls.location_display,
      location_lat: cls.location_lat,
      location_lng: cls.location_lng,
      day_of_week: cls.day_of_week,
      time: cls.time,
      duration_minutes: cls.duration_minutes,
      min_age_months: cls.min_age_months,
      max_age_months: cls.max_age_months,
      with_parent: cls.with_parent !== false
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      class_type: "under_2",
      location_id: "",
      location_address: "",
      location_display: "",
      location_lat: null,
      location_lng: null,
      day_of_week: "Monday",
      time: "09:30",
      duration_minutes: 45,
      min_age_months: 0,
      max_age_months: 23,
      with_parent: true
    });
    setBulkMode(false);
    setSelectedDays([]);
  };

  // Opening hours handlers
  const openEditHours = (locationId) => {
    const existing = openingHours[locationId] || DEFAULT_HOURS;
    setHoursForm(existing.map(h => ({ ...h })));
    setEditingLocation(locationId);
  };

  const saveHours = async () => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.put(`${API}/admin/opening-hours/${editingLocation}?pin=${storedPin}`, hoursForm);
      toast.success("Opening hours updated!");
      fetchOpeningHours();
      setEditingLocation(null);
    } catch (error) {
      toast.error("Failed to save hours");
    }
  };

  // Closed dates handlers
  const addClosedDate = async () => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.post(`${API}/admin/closed-dates?pin=${storedPin}`, closedForm);
      toast.success("Closed date added!");
      fetchClosedDates();
      setShowAddClosed(false);
      setClosedForm({ date: "", reason: "Bank Holiday", applies_to: ["all"] });
    } catch (error) {
      toast.error("Failed to add closed date");
    }
  };

  const deleteClosedDate = async (id) => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.delete(`${API}/admin/closed-dates/${id}?pin=${storedPin}`);
      toast.success("Closed date removed!");
      fetchClosedDates();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Term dates handlers
  const addTermDate = async () => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.post(`${API}/admin/term-dates?pin=${storedPin}`, termForm);
      toast.success("Term dates added!");
      fetchTermDates();
      setShowAddTerm(false);
      setTermForm({ name: "", start_date: "", end_date: "" });
    } catch (error) {
      toast.error("Failed to add term dates");
    }
  };

  const deleteTermDate = async (id) => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.delete(`${API}/admin/term-dates/${id}?pin=${storedPin}`);
      toast.success("Term removed!");
      fetchTermDates();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Location handlers
  const handleLocationSelect = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    if (loc) {
      setFormData(prev => ({
        ...prev,
        location_id: loc.id,
        location_display: loc.display_name,
        location_address: loc.address,
        location_lat: loc.lat,
        location_lng: loc.lng
      }));
    }
  };

  const resetLocForm = () => {
    setLocForm({ name: "", display_name: "", address: "", postcode: "", lat: null, lng: null });
    setEditingLoc(null);
  };

  const saveLocation = async () => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      if (editingLoc) {
        await axios.put(`${API}/admin/locations/${editingLoc.id}?pin=${storedPin}`, locForm);
        toast.success("Location updated!");
      } else {
        await axios.post(`${API}/admin/locations?pin=${storedPin}`, locForm);
        toast.success("Location added!");
      }
      fetchLocations();
      setShowAddLocation(false);
      resetLocForm();
    } catch (error) {
      toast.error("Failed to save location");
    }
  };

  const editLocation = (loc) => {
    setEditingLoc(loc);
    setLocForm({
      name: loc.name,
      display_name: loc.display_name,
      address: loc.address,
      postcode: loc.postcode,
      lat: loc.lat,
      lng: loc.lng
    });
    setShowAddLocation(true);
  };

  const deleteLocation = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.delete(`${API}/admin/locations/${id}?pin=${storedPin}`);
      toast.success("Location deleted!");
      fetchLocations();
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  // ========== COACHES ==========
  const fetchCoaches = async () => {
    try {
      const storedPin = localStorage.getItem("adminPin");
      const response = await axios.get(`${API}/admin/coaches?pin=${storedPin}`);
      setCoaches(response.data);
    } catch (error) { console.error("Error fetching coaches:", error); }
  };

  const resetCoachForm = () => { setCoachForm({ full_name: "", position: "", bio: "", qualifications: "", order: 0 }); setCoachImage(null); setEditingCoach(null); };

  const saveCoach = async () => {
    const storedPin = localStorage.getItem("adminPin");
    const fd = new FormData();
    fd.append("full_name", coachForm.full_name);
    fd.append("position", coachForm.position);
    fd.append("bio", coachForm.bio);
    fd.append("qualifications", coachForm.qualifications);
    fd.append("order", coachForm.order);
    if (coachImage) fd.append("image", coachImage);
    try {
      if (editingCoach) {
        await axios.put(`${API}/admin/coaches/${editingCoach.id}?pin=${storedPin}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Coach updated!");
      } else {
        await axios.post(`${API}/admin/coaches?pin=${storedPin}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Coach added!");
      }
      fetchCoaches();
      setShowAddCoach(false);
      resetCoachForm();
    } catch (error) { toast.error("Failed to save coach"); }
  };

  const editCoachItem = (c) => {
    setEditingCoach(c);
    setCoachForm({ full_name: c.full_name, position: c.position, bio: c.bio || "", qualifications: c.qualifications || "", order: c.order || 0 });
    setCoachImage(null);
    setShowAddCoach(true);
  };

  const deleteCoach = async (id) => {
    if (!window.confirm("Delete this coach?")) return;
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.delete(`${API}/admin/coaches/${id}?pin=${storedPin}`);
      toast.success("Coach deleted!");
      fetchCoaches();
    } catch (error) { toast.error("Failed to delete coach"); }
  };

  // ========== FAQs ==========
  const fetchFAQs = async () => {
    try {
      const storedPin = localStorage.getItem("adminPin");
      const response = await axios.get(`${API}/admin/faqs?pin=${storedPin}`);
      setFaqs(response.data);
    } catch (error) { console.error("Error fetching FAQs:", error); }
  };

  const resetFAQForm = () => { setFaqForm({ question: "", answer: "", order: 0 }); setEditingFAQ(null); };

  const saveFAQ = async () => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      if (editingFAQ) {
        await axios.put(`${API}/admin/faqs/${editingFAQ.id}?pin=${storedPin}`, faqForm);
        toast.success("FAQ updated!");
      } else {
        await axios.post(`${API}/admin/faqs?pin=${storedPin}`, faqForm);
        toast.success("FAQ added!");
      }
      fetchFAQs();
      setShowAddFAQ(false);
      resetFAQForm();
    } catch (error) { toast.error("Failed to save FAQ"); }
  };

  const editFAQItem = (f) => {
    setEditingFAQ(f);
    setFaqForm({ question: f.question, answer: f.answer, order: f.order || 0 });
    setShowAddFAQ(true);
  };

  const deleteFAQ = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.delete(`${API}/admin/faqs/${id}?pin=${storedPin}`);
      toast.success("FAQ deleted!");
      fetchFAQs();
    } catch (error) { toast.error("Failed to delete FAQ"); }
  };

  // ========== FOOTER TEXT ==========
  const fetchFooterText = async () => {
    try {
      const response = await axios.get(`${API}/footer-text`);
      setFooterText(response.data);
    } catch (error) { console.error("Error fetching footer text:", error); }
  };

  const saveFooterText = async () => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.put(`${API}/admin/footer-text?pin=${storedPin}`, footerText);
      toast.success("Footer text updated!");
    } catch (error) { toast.error("Failed to save footer text"); }
  };

  // Social links handlers
  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-links`);
      setSocialLinks(response.data);
    } catch (error) { console.error("Error fetching social links:", error); }
  };

  const saveSocialLinks = async () => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.put(`${API}/admin/social-links?pin=${storedPin}`, socialLinks);
      toast.success("Social links updated!");
    } catch (error) { toast.error("Failed to save social links"); }
  };

  // What to Expect handlers
  const WTE_ICONS = ["Shirt", "Baby", "Clock", "Car", "Heart", "AlertCircle", "Star", "Shield", "Users", "BookOpen", "Smile", "ThumbsUp"];

  const fetchWhatToExpect = async () => {
    try {
      const response = await axios.get(`${API}/what-to-expect`);
      setWhatToExpect(response.data);
    } catch (error) { console.error("Error fetching what to expect:", error); }
  };

  const saveWhatToExpect = async (items) => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.put(`${API}/admin/what-to-expect?pin=${storedPin}`, items);
      toast.success("What to Expect updated!");
      setWhatToExpect(items);
    } catch (error) { toast.error("Failed to save"); }
  };

  const handleAddWTE = () => {
    const updated = [...whatToExpect, { ...wteForm }];
    saveWhatToExpect(updated);
    setWteForm({ icon: "Shirt", title: "", content: "", accent: "red" });
  };

  const handleDeleteWTE = (index) => {
    const updated = whatToExpect.filter((_, i) => i !== index);
    saveWhatToExpect(updated);
  };

  const handleSaveEditWTE = () => {
    const updated = whatToExpect.map((item, i) => i === editingWTE ? { ...wteForm } : item);
    saveWhatToExpect(updated);
    setEditingWTE(null);
    setWteForm({ icon: "Shirt", title: "", content: "", accent: "red" });
  };

  // Birthday Parties handlers
  const fetchBirthdayParties = async () => {
    try {
      const response = await axios.get(`${API}/birthday-parties`);
      setBirthdayParties(response.data);
    } catch (error) { console.error("Error fetching birthday parties:", error); }
  };

  const saveBirthdayParties = async (items) => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.put(`${API}/admin/birthday-parties?pin=${storedPin}`, items);
      toast.success("Birthday parties updated!");
      setBirthdayParties(items);
    } catch (error) { toast.error("Failed to save"); }
  };

  const handleAddParty = () => {
    const newParty = { ...partyForm, id: partyForm.id || partyForm.name.toLowerCase().replace(/\s+/g, "_"), order: birthdayParties.length };
    saveBirthdayParties([...birthdayParties, newParty]);
    setPartyForm({ id: "", name: "", member_price: "", non_member_price: "" });
  };

  const handleDeleteParty = (index) => {
    saveBirthdayParties(birthdayParties.filter((_, i) => i !== index));
  };

  const handleSaveEditParty = () => {
    const updated = birthdayParties.map((item, i) => i === editingParty ? { ...partyForm } : item);
    saveBirthdayParties(updated);
    setEditingParty(null);
    setPartyForm({ id: "", name: "", member_price: "", non_member_price: "" });
  };

  // Location detail save
  const saveLocationDetails = async (locId, details) => {
    const storedPin = localStorage.getItem("adminPin");
    const loc = locations.find(l => l.id === locId);
    if (!loc) return;
    try {
      await axios.put(`${API}/admin/locations/${locId}?pin=${storedPin}`, { ...loc, ...details });
      toast.success("Location updated!");
      fetchLocations();
    } catch (error) { toast.error("Failed to save location"); }
  };

  // Terms handlers
  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/terms`);
      setTermsSections(response.data);
    } catch (error) { console.error("Error fetching terms:", error); }
  };

  const saveTerms = async (items) => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.put(`${API}/admin/terms?pin=${storedPin}`, items);
      toast.success("Terms updated!");
      setTermsSections(items);
    } catch (error) { toast.error("Failed to save terms"); }
  };

  const handleAddTerm = () => {
    const newSection = { title: tcForm.title, items: tcForm.items.split("\n").map(s => s.trim()).filter(Boolean) };
    saveTerms([...termsSections, newSection]);
    setTcForm({ title: "", items: "" });
  };

  const handleDeleteTerm = (index) => {
    saveTerms(termsSections.filter((_, i) => i !== index));
  };

  const handleSaveEditTerm = () => {
    const updated = termsSections.map((s, i) => i === editingTermIdx ? { title: tcForm.title, items: tcForm.items.split("\n").map(x => x.trim()).filter(Boolean) } : s);
    saveTerms(updated);
    setEditingTermIdx(null);
    setTcForm({ title: "", items: "" });
  };

  // Page content handlers
  const fetchPageContent = async () => {
    try {
      const [about, contact, hero] = await Promise.all([
        axios.get(`${API}/page/about`),
        axios.get(`${API}/page/contact`),
        axios.get(`${API}/page/hero`),
      ]);
      setAboutContent(about.data);
      setContactContent(contact.data);
      setHeroContent(hero.data);
    } catch (error) { console.error("Error fetching page content:", error); }
  };

  const savePageContent = async (page, data, setter) => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.put(`${API}/admin/page/${page}?pin=${storedPin}`, data);
      toast.success(`${page} page updated!`);
      setter(data);
    } catch (error) { toast.error("Failed to save"); }
  };

  // Booking links handlers
  const fetchBookingLinks = async () => {
    try {
      const res = await axios.get(`${API}/booking-links`);
      setBookingLinks(res.data);
    } catch (error) { console.error("Error fetching booking links:", error); }
  };

  const saveBookingLinks = async () => {
    const storedPin = localStorage.getItem("adminPin");
    try {
      await axios.put(`${API}/admin/booking-links?pin=${storedPin}`, bookingLinks);
      toast.success("Booking links updated!");
    } catch (error) { toast.error("Failed to save"); }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-lg max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#ff3131]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#ff3131]" />
            </div>
            <h1 className="text-2xl font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>
              Admin Login
            </h1>
            <p className="text-sm text-[#636E72] mt-1">Enter your 4-digit PIN</p>
          </div>
          
          <div className="space-y-4">
            <input
              type="password"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••"
              className="w-full text-center text-3xl tracking-[1em] p-4 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] focus:outline-none"
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              data-testid="admin-pin-input"
            />
            <button
              onClick={handleLogin}
              className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold transition-colors"
              style={{ fontFamily: 'Fredoka, cursive' }}
              data-testid="admin-login-btn"
            >
              Login
            </button>
          </div>
          
          <a href="/" className="block text-center text-sm text-[#636E72] mt-6 hover:text-[#ff3131]">
            ← Back to website
          </a>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/api/media/gymtots-logo.png" alt="Gymtots" className="h-10" />
            <span className="text-sm font-semibold text-[#636E72]" style={{ fontFamily: 'Fredoka, cursive' }}>Admin</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#636E72] hover:text-[#ff3131] text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: "classes", label: `Classes (${classes.length})` },
            { id: "events", label: "Events" },
            { id: "booking-links", label: "Booking Links" },
            { id: "locations", label: `Locations (${locations.length})` },
            { id: "coaches", label: `Coaches (${coaches.length})` },
            { id: "hours", label: "Opening Hours" },
            { id: "birthday", label: "Birthday Parties" },
            { id: "faqs", label: `FAQs (${faqs.length})` },
            { id: "footer", label: "Footer Text" },
            { id: "social", label: "Social Links" },
            { id: "what-to-expect", label: `What to Expect (${whatToExpect.length})` },
            { id: "terms", label: `Terms (${termsSections.length})` },
            { id: "about", label: "About Page" },
            { id: "contact", label: "Contact Page" },
            { id: "hero", label: "Homepage Hero" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                activeTab === tab.id 
                  ? "bg-[#ff3131] text-white" 
                  : "bg-white text-[#636E72] hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Classes Tab */}
        {activeTab === "classes" && (
          <>
            {!showAddForm && (
              <button
                onClick={() => { setShowAddForm(true); setEditingClass(null); resetForm(); }}
                className="mb-6 flex items-center gap-2 bg-[#7dbff8] hover:bg-[#6ca6d9] text-white px-5 py-3 rounded-full font-bold transition-colors"
                style={{ fontFamily: 'Fredoka, cursive' }}
                data-testid="add-class-btn"
              >
                <Plus className="w-5 h-5" />
                Add New Class
              </button>
            )}

            {showAddForm && (
              <div className="bg-white rounded-2xl p-6 mb-6 border-2 border-[#7dbff8]">
                <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
                  {editingClass ? "Edit Class" : "Add New Class"}
                </h2>

                {/* Bulk Add toggle — only when adding, not editing */}
                {!editingClass && (
                  <div className="flex items-center gap-3 mb-4 bg-[#FFFBF5] rounded-xl p-3 border border-gray-100">
                    <button
                      type="button"
                      onClick={() => { setBulkMode(false); setSelectedDays([]); }}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${!bulkMode ? 'bg-[#ff3131] text-white' : 'bg-white text-[#636E72] border border-gray-200'}`}
                      data-testid="single-add-btn"
                    >
                      Single Day
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkMode(true)}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${bulkMode ? 'bg-[#ff3131] text-white' : 'bg-white text-[#636E72] border border-gray-200'}`}
                      data-testid="bulk-add-btn"
                    >
                      Bulk Add (Multiple Days)
                    </button>
                    {bulkMode && <span className="text-xs text-[#636E72]">Select the days this class runs</span>}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1">Class Type</label>
                      <select
                        value={formData.class_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, class_type: e.target.value }))}
                        className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none"
                      >
                        {Object.entries(CLASS_TYPES).map(([key, val]) => (
                          <option key={key} value={key}>{val.name} - £{val.price.toFixed(2)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1">{bulkMode && !editingClass ? "Days" : "Day"}</label>
                      {bulkMode && !editingClass ? (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {DAYS.map(day => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                setSelectedDays(prev => 
                                  prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
                                );
                              }}
                              className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
                                selectedDays.includes(day)
                                  ? 'border-[#ff3131] bg-[#ff3131]/10 text-[#ff3131]'
                                  : 'border-gray-100 text-[#636E72] hover:border-gray-200'
                              }`}
                              data-testid={`bulk-day-${day.toLowerCase()}`}
                            >
                              {day.slice(0, 3)}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <select
                          value={formData.day_of_week}
                          onChange={(e) => setFormData(prev => ({ ...prev, day_of_week: e.target.value }))}
                          className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none"
                        >
                          {DAYS.map(day => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1">Time</label>
                      <input
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                        className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1">Duration (mins)</label>
                      <input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                        className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1">Class Type</label>
                      <div className="flex gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, with_parent: true }))}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${formData.with_parent ? 'border-[#7dbff8] bg-[#7dbff8]/10 text-[#7dbff8]' : 'border-gray-100 text-[#636E72]'}`}
                          data-testid="with-parent-btn"
                        >
                          With Parent
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, with_parent: false }))}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${!formData.with_parent ? 'border-[#ff3131] bg-[#ff3131]/10 text-[#ff3131]' : 'border-gray-100 text-[#636E72]'}`}
                          data-testid="independent-btn"
                        >
                          Independent
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1">Min Age (months)</label>
                      <input
                        type="number"
                        value={formData.min_age_months}
                        onChange={(e) => setFormData(prev => ({ ...prev, min_age_months: parseInt(e.target.value) || 0 }))}
                        className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1">Max Age (months)</label>
                      <input
                        type="number"
                        value={formData.max_age_months}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_age_months: parseInt(e.target.value) || 0 }))}
                        className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1">Location</label>
                      <select
                        value={formData.location_id}
                        onChange={(e) => handleLocationSelect(e.target.value)}
                        className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none"
                        data-testid="class-location-select"
                      >
                        <option value="">-- Select location --</option>
                        {locations.filter(loc => loc.id !== "head_office").map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.display_name}</option>
                        ))}
                      </select>
                      {formData.location_id && (
                        <p className="text-xs text-[#636E72] mt-1">
                          {formData.location_address}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      className="flex-1 bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold transition-colors"
                      style={{ fontFamily: 'Fredoka, cursive' }}
                    >
                      {editingClass ? "Update Class" : (bulkMode && selectedDays.length > 1 ? `Add ${selectedDays.length} Classes` : "Add Class")}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAddForm(false); setEditingClass(null); }}
                      className="px-6 py-3 bg-gray-100 text-[#636E72] rounded-full font-bold hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-3">
              {classes.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <Calendar className="w-12 h-12 text-[#636E72] mx-auto mb-3" />
                  <p className="text-[#636E72]">No classes yet. Add your first class!</p>
                </div>
              ) : (
                classes.map((cls) => (
                  <div key={cls.id} className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>
                            {cls.class_name}
                          </span>
                          <span className="text-xs bg-[#7dbff8]/20 text-[#7dbff8] px-2 py-0.5 rounded-full font-semibold">
                            £{cls.price.toFixed(2)}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-3 text-xs text-[#636E72]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {cls.day_of_week} {cls.time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {cls.duration_minutes} mins
                          </span>
                          <span className="flex items-center gap-1">
                            <Baby className="w-3.5 h-3.5" />
                            {cls.min_age_months}-{cls.max_age_months} months
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {cls.location_display}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(cls)}
                          className="p-2 text-[#7dbff8] hover:bg-[#7dbff8]/10 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(cls.id)}
                          className="p-2 text-[#ff3131] hover:bg-[#ff3131]/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Booking Links Tab */}
        {/* Events Tab */}
        {activeTab === "events" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Events</h2>
            <EventsAdminTab pin={pin} />
          </div>
        )}

        {activeTab === "booking-links" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-2" style={{ fontFamily: 'Fredoka, cursive' }}>Booking Links</h2>
            <p className="text-sm text-[#636E72] mb-6">Set individual LoveAdmin/Joinin links for each class type. If a class type has no specific link, the general link is used.</p>
            <div className="space-y-4 mb-6">
              <div className="bg-[#ff3131]/5 rounded-xl p-4 border border-[#ff3131]/10">
                <label className="block text-sm font-bold text-[#ff3131] mb-1">General Booking Link</label>
                <p className="text-xs text-[#636E72] mb-2">Used for "Book a Taster Session" buttons, birthday parties, and anywhere a specific class isn't selected.</p>
                <input type="url" value={bookingLinks.general || ""} onChange={(e) => setBookingLinks(p => ({ ...p, general: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none text-sm" placeholder="https://app.joinin.online/..." data-testid="booking-link-general" />
              </div>
              {Object.entries(CLASS_TYPES).map(([key, val]) => (
                <div key={key}>
                  <label className="block text-xs font-semibold text-[#2D3436] mb-1">{val.name}</label>
                  <input type="url" value={bookingLinks[key] || ""} onChange={(e) => setBookingLinks(p => ({ ...p, [key]: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="Leave blank to use general link" data-testid={`booking-link-${key}`} />
                </div>
              ))}
            </div>
            <button onClick={saveBookingLinks} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-6 py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="save-booking-links-btn">Save Booking Links</button>
          </div>
        )}

        {/* Opening Hours Tab */}
        {activeTab === "hours" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
                <Settings className="w-5 h-5 inline mr-2" />
                Opening Hours by Location
              </h2>
              
              <div className="grid md:grid-cols-3 gap-4">
                {locations.map(loc => (
                  <div key={loc.id} className="bg-[#FFFBF5] rounded-xl p-4">
                    <h3 className="font-bold text-[#2D3436] mb-3" style={{ fontFamily: 'Fredoka, cursive' }}>
                      {loc.display_name}
                    </h3>
                    
                    <div className="space-y-1 text-sm mb-4">
                      {(openingHours[loc.id] || DEFAULT_HOURS).map((h, i) => (
                        <div key={i} className={`flex justify-between ${h.is_open ? 'text-[#636E72]' : 'text-[#ff3131]'}`}>
                          <span>{h.day}</span>
                          <span>{h.is_open ? `${h.open_time} - ${h.close_time}` : 'Closed'}</span>
                        </div>
                      ))}
                    </div>
                    
                    <button
                      onClick={() => openEditHours(loc.id)}
                      className="w-full py-2 bg-[#7dbff8] hover:bg-[#6ca6d9] text-white rounded-full text-sm font-bold transition-colors"
                    >
                      Edit Hours
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Edit Hours Modal */}
            {editingLocation && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>
                      Edit Hours - {locations.find(l => l.id === editingLocation)?.display_name}
                    </h3>
                    <button onClick={() => setEditingLocation(null)} className="p-2 hover:bg-gray-100 rounded-full">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {hoursForm.map((h, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="w-20 text-sm font-semibold">{h.day}</span>
                        <label className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={h.is_open}
                            onChange={(e) => {
                              const updated = [...hoursForm];
                              updated[i].is_open = e.target.checked;
                              setHoursForm(updated);
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-xs">Open</span>
                        </label>
                        {h.is_open && (
                          <>
                            <input
                              type="time"
                              value={h.open_time || "09:00"}
                              onChange={(e) => {
                                const updated = [...hoursForm];
                                updated[i].open_time = e.target.value;
                                setHoursForm(updated);
                              }}
                              className="p-1 border rounded text-sm"
                            />
                            <span>-</span>
                            <input
                              type="time"
                              value={h.close_time || "16:00"}
                              onChange={(e) => {
                                const updated = [...hoursForm];
                                updated[i].close_time = e.target.value;
                                setHoursForm(updated);
                              }}
                              className="p-1 border rounded text-sm"
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={saveHours}
                    className="w-full mt-6 py-3 bg-[#ff3131] hover:bg-[#e62e2e] text-white rounded-full font-bold"
                  >
                    Save Hours
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Locations Tab - Editable Details */}
        {activeTab === "locations" && (
          <div className="space-y-6">
            {locations.filter(l => l.id !== "head_office").map(loc => (
              <div key={loc.id} className="bg-white rounded-2xl p-6 border border-gray-100" data-testid={`admin-location-${loc.id}`}>
                <h3 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>{loc.display_name}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#2D3436] mb-1">Display Name</label>
                    <input type="text" defaultValue={loc.display_name} id={`loc-display-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2D3436] mb-1">Address</label>
                    <input type="text" defaultValue={loc.address} id={`loc-address-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2D3436] mb-1">Postcode</label>
                    <input type="text" defaultValue={loc.postcode} id={`loc-postcode-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2D3436] mb-1">Phone</label>
                    <input type="text" defaultValue={loc.phone || ""} id={`loc-phone-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="e.g. 02380 455007" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2D3436] mb-1">Parking Info</label>
                    <input type="text" defaultValue={loc.parking || ""} id={`loc-parking-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="e.g. Free parking available" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2D3436] mb-1">Nearby / Description</label>
                    <input type="text" defaultValue={loc.nearby || ""} id={`loc-nearby-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="e.g. Near Hamble village centre" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2D3436] mb-1">Google Maps URL</label>
                    <input type="text" defaultValue={loc.map_url || ""} id={`loc-mapurl-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="https://maps.google.com/..." />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-[#2D3436] mb-1">Latitude</label>
                      <input type="number" step="any" defaultValue={loc.lat || ""} id={`loc-lat-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#2D3436] mb-1">Longitude</label>
                      <input type="number" step="any" defaultValue={loc.lng || ""} id={`loc-lng-${loc.id}`} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" />
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const get = (field) => document.getElementById(`loc-${field}-${loc.id}`)?.value || "";
                    saveLocationDetails(loc.id, {
                      display_name: get("display"), name: loc.name,
                      address: get("address"), postcode: get("postcode"),
                      phone: get("phone"), parking: get("parking"),
                      nearby: get("nearby"), map_url: get("mapurl"),
                      lat: parseFloat(document.getElementById(`loc-lat-${loc.id}`)?.value) || null,
                      lng: parseFloat(document.getElementById(`loc-lng-${loc.id}`)?.value) || null,
                    });
                  }}
                  className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-5 py-2 rounded-full font-bold text-sm transition-colors"
                  style={{ fontFamily: 'Fredoka, cursive' }}
                  data-testid={`save-location-${loc.id}`}
                >
                  Save {loc.display_name}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Birthday Parties Tab */}
        {activeTab === "birthday" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Birthday Party Packages</h2>
            
            <div className="bg-[#FFFBF5] rounded-xl p-4 mb-6 border border-gray-100">
              <h3 className="font-bold text-sm text-[#2D3436] mb-3">{editingParty !== null ? "Edit Package" : "Add New Package"}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-semibold text-[#2D3436] mb-1">Party Name</label>
                  <input type="text" value={partyForm.name} onChange={(e) => setPartyForm(p => ({ ...p, name: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="e.g. Big Gym Party" data-testid="party-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#2D3436] mb-1">Member Price</label>
                  <input type="text" value={partyForm.member_price} onChange={(e) => setPartyForm(p => ({ ...p, member_price: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="e.g. £180" data-testid="party-member-price" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#2D3436] mb-1">Non-Member Price</label>
                  <input type="text" value={partyForm.non_member_price} onChange={(e) => setPartyForm(p => ({ ...p, non_member_price: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="e.g. £200" data-testid="party-nonmember-price" />
                </div>
              </div>
              {editingParty !== null ? (
                <div className="flex gap-2">
                  <button onClick={handleSaveEditParty} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-5 py-2 rounded-full font-bold text-sm" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="party-save-edit-btn">Save Changes</button>
                  <button onClick={() => { setEditingParty(null); setPartyForm({ id: "", name: "", member_price: "", non_member_price: "" }); }} className="bg-gray-100 text-[#2D3436] px-5 py-2 rounded-full font-bold text-sm">Cancel</button>
                </div>
              ) : (
                <button onClick={handleAddParty} disabled={!partyForm.name} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-5 py-2 rounded-full font-bold text-sm disabled:opacity-40" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="party-add-btn">Add Package</button>
              )}
            </div>

            <div className="space-y-3">
              {birthdayParties.map((party, i) => (
                <div key={i} className="flex items-center justify-between bg-[#FFFBF5] rounded-xl p-4 border border-gray-50" data-testid={`party-admin-${i}`}>
                  <div>
                    <h4 className="font-bold text-sm text-[#2D3436]">{party.name}</h4>
                    <p className="text-xs text-[#636E72]">Members: {party.member_price} · Non-Members: {party.non_member_price}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditingParty(i); setPartyForm({ ...party }); }} className="p-2 text-[#7dbff8] hover:bg-[#7dbff8]/10 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteParty(i)} className="p-2 text-[#ff3131] hover:bg-[#ff3131]/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {birthdayParties.length === 0 && <p className="text-sm text-[#636E72] text-center py-4">No party packages yet. Add your first one above.</p>}
            </div>
          </div>
        )}

        {/* Coaches Tab */}
        {activeTab === "coaches" && (
          <div className="space-y-4">
            {!showAddCoach && (
              <button onClick={() => { setShowAddCoach(true); resetCoachForm(); }} className="mb-2 flex items-center gap-2 bg-[#7dbff8] hover:bg-[#6ca6d9] text-white px-5 py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="add-coach-btn">
                <Plus className="w-5 h-5" /> Add Coach
              </button>
            )}

            {showAddCoach && (
              <div className="bg-white rounded-2xl p-6 border-2 border-[#7dbff8]">
                <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>{editingCoach ? "Edit Coach" : "Add Coach"}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">Full Name</label>
                    <input type="text" value={coachForm.full_name} onChange={(e) => setCoachForm(p => ({ ...p, full_name: e.target.value }))} placeholder="e.g. Sarah Johnson" className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" data-testid="coach-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">Position</label>
                    <input type="text" value={coachForm.position} onChange={(e) => setCoachForm(p => ({ ...p, position: e.target.value }))} placeholder="e.g. Head Coach" className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" data-testid="coach-position-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">About</label>
                    <textarea value={coachForm.bio} onChange={(e) => setCoachForm(p => ({ ...p, bio: e.target.value }))} placeholder="A bit about them..." rows={3} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none resize-none" data-testid="coach-bio-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">Qualifications <span className="text-xs font-normal text-[#636E72]">(comma separated)</span></label>
                    <input type="text" value={coachForm.qualifications} onChange={(e) => setCoachForm(p => ({ ...p, qualifications: e.target.value }))} placeholder="e.g. Level 2 Gymnastics, First Aid, DBS Checked" className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" data-testid="coach-qualifications-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">Photo</label>
                    <label className="flex items-center gap-3 p-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#7dbff8] transition-colors">
                      <Upload className="w-5 h-5 text-[#636E72]" />
                      <span className="text-sm text-[#636E72]">{coachImage ? coachImage.name : (editingCoach?.image_url ? "Change photo..." : "Upload photo...")}</span>
                      <input type="file" onChange={(e) => setCoachImage(e.target.files[0])} className="hidden" data-testid="coach-image-input" />
                    </label>
                    {editingCoach?.image_url && !coachImage && (
                      <img src={editingCoach.image_url} alt="" className="w-16 h-16 rounded-xl object-cover mt-2" />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">Display Order</label>
                    <input type="number" value={coachForm.order} onChange={(e) => setCoachForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={saveCoach} className="flex-1 bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="save-coach-btn">{editingCoach ? "Update Coach" : "Add Coach"}</button>
                  <button onClick={() => { setShowAddCoach(false); resetCoachForm(); }} className="px-6 py-3 bg-gray-100 text-[#636E72] rounded-full font-bold hover:bg-gray-200 transition-colors">Cancel</button>
                </div>
              </div>
            )}

            {coaches.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <Users className="w-12 h-12 text-[#636E72] mx-auto mb-3" />
                <p className="text-[#636E72]">No coaches yet. Add your first!</p>
              </div>
            ) : (
              coaches.map(c => (
                <div key={c.id} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-4" data-testid={`coach-${c.id}`}>
                  {c.image_url ? (
                    <img src={`${BACKEND_URL}${c.image_url}`} alt={c.full_name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-[#7dbff8]/20 flex items-center justify-center flex-shrink-0"><Users className="w-6 h-6 text-[#7dbff8]" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>{c.full_name}</p>
                    <p className="text-xs text-[#7dbff8] font-semibold">{c.position}</p>
                    {c.bio && <p className="text-xs text-[#636E72] mt-1 line-clamp-1">{c.bio}</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => editCoachItem(c)} className="p-2 text-[#7dbff8] hover:bg-[#7dbff8]/10 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deleteCoach(c.id)} className="p-2 text-[#ff3131] hover:bg-[#ff3131]/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* FAQs Tab */}
        {activeTab === "faqs" && (
          <div className="space-y-4">
            {!showAddFAQ && (
              <button onClick={() => { setShowAddFAQ(true); resetFAQForm(); }} className="mb-2 flex items-center gap-2 bg-[#7dbff8] hover:bg-[#6ca6d9] text-white px-5 py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="add-faq-btn">
                <Plus className="w-5 h-5" /> Add FAQ
              </button>
            )}

            {showAddFAQ && (
              <div className="bg-white rounded-2xl p-6 border-2 border-[#7dbff8]">
                <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>{editingFAQ ? "Edit FAQ" : "Add FAQ"}</h2>
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">Question</label>
                    <input type="text" value={faqForm.question} onChange={(e) => setFaqForm(p => ({ ...p, question: e.target.value }))} placeholder="e.g. What age can my child start?" className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" data-testid="faq-question-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">Answer</label>
                    <textarea value={faqForm.answer} onChange={(e) => setFaqForm(p => ({ ...p, answer: e.target.value }))} placeholder="The answer..." rows={4} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none resize-none" data-testid="faq-answer-input" />
                  </div>
                  <div className="w-32">
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1">Order</label>
                    <input type="number" value={faqForm.order} onChange={(e) => setFaqForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={saveFAQ} className="flex-1 bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="save-faq-btn">{editingFAQ ? "Update FAQ" : "Add FAQ"}</button>
                  <button onClick={() => { setShowAddFAQ(false); resetFAQForm(); }} className="px-6 py-3 bg-gray-100 text-[#636E72] rounded-full font-bold hover:bg-gray-200 transition-colors">Cancel</button>
                </div>
              </div>
            )}

            {faqs.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <p className="text-[#636E72]">No FAQs yet. Add your first!</p>
              </div>
            ) : (
              faqs.map(f => (
                <div key={f.id} className="bg-white rounded-xl p-4 border border-gray-100" data-testid={`faq-${f.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-bold text-[#2D3436] text-sm" style={{ fontFamily: 'Fredoka, cursive' }}>{f.question}</p>
                      <p className="text-xs text-[#636E72] mt-1 line-clamp-2">{f.answer}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => editFAQItem(f)} className="p-2 text-[#7dbff8] hover:bg-[#7dbff8]/10 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => deleteFAQ(f.id)} className="p-2 text-[#ff3131] hover:bg-[#ff3131]/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Emails Tab */}
        {/* Footer Text Tab */}
        {activeTab === "footer" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Footer Text</h2>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-[#2D3436] mb-1">Tagline</label>
                <input type="text" value={footerText.tagline} onChange={(e) => setFooterText(p => ({ ...p, tagline: e.target.value }))} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" data-testid="footer-tagline-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D3436] mb-1">Copyright / Bottom Bar Text</label>
                <input type="text" value={footerText.copyright_text} onChange={(e) => setFooterText(p => ({ ...p, copyright_text: e.target.value }))} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" data-testid="footer-copyright-input" />
              </div>
            </div>
            <button onClick={saveFooterText} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-6 py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="save-footer-btn">Save Footer Text</button>
          </div>
        )}

        {/* Social Links Tab */}
        {activeTab === "social" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Social Media Links</h2>
            <p className="text-sm text-[#636E72] mb-4">Add your Facebook and Instagram page URLs. Icons will only appear in the footer when a URL is provided.</p>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-[#2D3436] mb-1">Facebook URL</label>
                <input type="url" value={socialLinks.facebook_url} onChange={(e) => setSocialLinks(p => ({ ...p, facebook_url: e.target.value }))} placeholder="https://www.facebook.com/yourpage" className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" data-testid="social-facebook-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D3436] mb-1">Instagram URL</label>
                <input type="url" value={socialLinks.instagram_url} onChange={(e) => setSocialLinks(p => ({ ...p, instagram_url: e.target.value }))} placeholder="https://www.instagram.com/yourpage" className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none" data-testid="social-instagram-input" />
              </div>
            </div>
            <button onClick={saveSocialLinks} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-6 py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="save-social-btn">Save Social Links</button>
          </div>
        )}

        {/* What to Expect Tab */}
        {activeTab === "what-to-expect" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>What to Expect Items</h2>
            
            {/* Add/Edit form */}
            <div className="bg-[#FFFBF5] rounded-xl p-4 mb-6 border border-gray-100">
              <h3 className="font-bold text-sm text-[#2D3436] mb-3">{editingWTE !== null ? "Edit Item" : "Add New Item"}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-semibold text-[#2D3436] mb-1">Icon</label>
                  <select value={wteForm.icon} onChange={(e) => setWteForm(p => ({ ...p, icon: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" data-testid="wte-icon-select">
                    {WTE_ICONS.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#2D3436] mb-1">Accent Colour</label>
                  <select value={wteForm.accent} onChange={(e) => setWteForm(p => ({ ...p, accent: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" data-testid="wte-accent-select">
                    <option value="red">Red</option>
                    <option value="blue">Blue</option>
                  </select>
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Title</label>
                <input type="text" value={wteForm.title} onChange={(e) => setWteForm(p => ({ ...p, title: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="e.g. What to Wear" data-testid="wte-title-input" />
              </div>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Content</label>
                <textarea value={wteForm.content} onChange={(e) => setWteForm(p => ({ ...p, content: e.target.value }))} rows={3} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="Description text..." data-testid="wte-content-input" />
              </div>
              {editingWTE !== null ? (
                <div className="flex gap-2">
                  <button onClick={handleSaveEditWTE} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-5 py-2 rounded-full font-bold text-sm transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="wte-save-edit-btn">Save Changes</button>
                  <button onClick={() => { setEditingWTE(null); setWteForm({ icon: "Shirt", title: "", content: "", accent: "red" }); }} className="bg-gray-100 text-[#2D3436] px-5 py-2 rounded-full font-bold text-sm" style={{ fontFamily: 'Fredoka, cursive' }}>Cancel</button>
                </div>
              ) : (
                <button onClick={handleAddWTE} disabled={!wteForm.title || !wteForm.content} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-5 py-2 rounded-full font-bold text-sm transition-colors disabled:opacity-40" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="wte-add-btn">Add Item</button>
              )}
            </div>

            {/* Existing items */}
            <div className="space-y-3">
              {whatToExpect.map((item, i) => (
                <div key={i} className="flex items-start justify-between gap-3 bg-[#FFFBF5] rounded-xl p-4 border border-gray-50" data-testid={`wte-admin-item-${i}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.accent === "red" ? "bg-[#ff3131]/10 text-[#ff3131]" : "bg-[#7dbff8]/10 text-[#7dbff8]"}`}>{item.icon}</span>
                      <h4 className="font-bold text-sm text-[#2D3436]">{item.title}</h4>
                    </div>
                    <p className="text-xs text-[#636E72]">{item.content}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditingWTE(i); setWteForm({ ...item }); }} className="p-2 text-[#7dbff8] hover:bg-[#7dbff8]/10 rounded-lg" data-testid={`wte-edit-${i}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteWTE(i)} className="p-2 text-[#ff3131] hover:bg-[#ff3131]/10 rounded-lg" data-testid={`wte-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {whatToExpect.length === 0 && (
                <p className="text-sm text-[#636E72] text-center py-4">No items yet. Add your first item above.</p>
              )}
            </div>
          </div>
        )}

        {/* Terms Tab */}
        {activeTab === "terms" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Terms & Conditions Sections</h2>
            
            <div className="bg-[#FFFBF5] rounded-xl p-4 mb-6 border border-gray-100">
              <h3 className="font-bold text-sm text-[#2D3436] mb-3">{editingTermIdx !== null ? "Edit Section" : "Add New Section"}</h3>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Section Title</label>
                <input type="text" value={tcForm.title} onChange={(e) => setTcForm(p => ({ ...p, title: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="e.g. 1. Booking & Payment" data-testid="term-title-input" />
              </div>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Items <span className="text-xs font-normal text-[#636E72]">(one per line)</span></label>
                <textarea value={tcForm.items} onChange={(e) => setTcForm(p => ({ ...p, items: e.target.value }))} rows={5} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder={"All bookings must be paid in advance\nWe accept card payments\nTrial sessions charged at standard rate"} data-testid="term-items-input" />
              </div>
              {editingTermIdx !== null ? (
                <div className="flex gap-2">
                  <button onClick={handleSaveEditTerm} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-5 py-2 rounded-full font-bold text-sm" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="term-save-edit-btn">Save Changes</button>
                  <button onClick={() => { setEditingTermIdx(null); setTcForm({ title: "", items: "" }); }} className="bg-gray-100 text-[#2D3436] px-5 py-2 rounded-full font-bold text-sm">Cancel</button>
                </div>
              ) : (
                <button onClick={handleAddTerm} disabled={!tcForm.title} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-5 py-2 rounded-full font-bold text-sm disabled:opacity-40" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="term-add-btn">Add Section</button>
              )}
            </div>

            <div className="space-y-3">
              {termsSections.map((section, i) => (
                <div key={i} className="flex items-start justify-between gap-3 bg-[#FFFBF5] rounded-xl p-4 border border-gray-50" data-testid={`term-admin-${i}`}>
                  <div className="flex-1">
                    <h4 className="font-bold text-sm text-[#2D3436] mb-1">{section.title}</h4>
                    <p className="text-xs text-[#636E72]">{(section.items || []).length} item{(section.items || []).length !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditingTermIdx(i); setTcForm({ title: section.title, items: (section.items || []).join("\n") }); }} className="p-2 text-[#7dbff8] hover:bg-[#7dbff8]/10 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteTerm(i)} className="p-2 text-[#ff3131] hover:bg-[#ff3131]/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {termsSections.length === 0 && <p className="text-sm text-[#636E72] text-center py-4">No sections yet. Add your first section above.</p>}
            </div>
          </div>
        )}

        {/* About Page Tab */}
        {activeTab === "about" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>About Us Page Content</h2>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Intro Text</label>
                <textarea value={aboutContent.intro || ""} onChange={(e) => setAboutContent(p => ({ ...p, intro: e.target.value }))} rows={2} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" data-testid="about-intro-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Philosophy Title</label>
                <input type="text" value={aboutContent.philosophy_title || ""} onChange={(e) => setAboutContent(p => ({ ...p, philosophy_title: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" data-testid="about-philosophy-title" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Philosophy Text <span className="text-xs font-normal text-[#636E72]">(use blank line for new paragraph)</span></label>
                <textarea value={aboutContent.philosophy_text || ""} onChange={(e) => setAboutContent(p => ({ ...p, philosophy_text: e.target.value }))} rows={5} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" data-testid="about-philosophy-text" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Affiliation Text</label>
                <textarea value={aboutContent.affiliation_text || ""} onChange={(e) => setAboutContent(p => ({ ...p, affiliation_text: e.target.value }))} rows={3} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" data-testid="about-affiliation-text" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-2">Highlights (Why Choose Gymtots)</label>
                {(aboutContent.highlights || []).map((h, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="text" value={h.title} onChange={(e) => { const hl = [...(aboutContent.highlights || [])]; hl[i] = { ...hl[i], title: e.target.value }; setAboutContent(p => ({ ...p, highlights: hl })); }} className="flex-1 p-2 border-2 border-gray-100 rounded-lg text-sm" placeholder="Title" />
                    <input type="text" value={h.text} onChange={(e) => { const hl = [...(aboutContent.highlights || [])]; hl[i] = { ...hl[i], text: e.target.value }; setAboutContent(p => ({ ...p, highlights: hl })); }} className="flex-[2] p-2 border-2 border-gray-100 rounded-lg text-sm" placeholder="Description" />
                    <button onClick={() => { const hl = (aboutContent.highlights || []).filter((_, j) => j !== i); setAboutContent(p => ({ ...p, highlights: hl })); }} className="p-2 text-[#ff3131]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => setAboutContent(p => ({ ...p, highlights: [...(p.highlights || []), { title: "", text: "" }] }))} className="text-sm text-[#7dbff8] font-semibold hover:underline">+ Add highlight</button>
              </div>
            </div>
            <button onClick={() => savePageContent("about", aboutContent, setAboutContent)} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-6 py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="save-about-btn">Save About Page</button>
          </div>
        )}

        {/* Contact Page Tab */}
        {activeTab === "contact" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Contact Page Content</h2>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Subtitle</label>
                <input type="text" value={contactContent.subtitle || ""} onChange={(e) => setContactContent(p => ({ ...p, subtitle: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" data-testid="contact-subtitle-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#2D3436] mb-1">Phone Number</label>
                  <input type="text" value={contactContent.phone || ""} onChange={(e) => setContactContent(p => ({ ...p, phone: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#2D3436] mb-1">Email Address</label>
                  <input type="text" value={contactContent.email || ""} onChange={(e) => setContactContent(p => ({ ...p, email: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" />
                </div>
              </div>
              <p className="text-xs text-[#636E72]">Phone hours automatically reflect Head Office opening hours.</p>
            </div>
            <button onClick={() => savePageContent("contact", contactContent, setContactContent)} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-6 py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="save-contact-btn">Save Contact Page</button>
          </div>
        )}

        {/* Homepage Hero Tab */}
        {activeTab === "hero" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Homepage Hero Content</h2>
            <div className="space-y-4 mb-4">
              {/* Hero Image Upload */}
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Hero Image</label>
                <div className="flex items-center gap-4">
                  {heroContent.image_url ? (
                    <img src={heroContent.image_url} alt="Hero" className="w-24 h-24 rounded-xl object-cover" />
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-[#636E72]/40" />
                    </div>
                  )}
                  <label className="cursor-pointer bg-[#FFFBF5] border-2 border-dashed border-gray-200 hover:border-[#7dbff8] rounded-xl px-4 py-3 transition-colors">
                    <span className="text-sm text-[#636E72] font-medium">{heroContent.image_url ? "Change image" : "Upload hero image"}</span>
                    <input type="file" className="hidden" data-testid="hero-image-upload" onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append("image", file);
                      const storedPin = localStorage.getItem("adminPin");
                      try {
                        const res = await axios.post(`${API}/admin/hero-image?pin=${storedPin}`, fd);
                        setHeroContent(p => ({ ...p, image_url: res.data.image_url }));
                        toast.success("Hero image updated!");
                      } catch { toast.error("Failed to upload image"); }
                    }} />
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Tagline</label>
                <input type="text" value={heroContent.tagline || ""} onChange={(e) => setHeroContent(p => ({ ...p, tagline: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="Be brave. Be curious. Be confident." data-testid="hero-tagline-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Description</label>
                <textarea value={heroContent.description || ""} onChange={(e) => setHeroContent(p => ({ ...p, description: e.target.value }))} rows={3} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" data-testid="hero-description-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Classes From Price</label>
                <input type="text" value={heroContent.price_from || ""} onChange={(e) => setHeroContent(p => ({ ...p, price_from: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="£8.50" data-testid="hero-price-input" />
              </div>
            </div>
            <button onClick={() => savePageContent("hero", heroContent, setHeroContent)} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-6 py-3 rounded-full font-bold transition-colors" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="save-hero-btn">Save Hero Content</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
